import { SxProps, Theme } from '@mui/material';

/**
 * Darstellungsvorgaben der Protokollzeilen (#1330).
 *
 * Die Zeile bestand aus einem `Stack` in Reihe: erst der Bereichs-Chip, dann
 * der Satz. Damit begann der Text bei jeder Zeile woanders — „Vorgang" ist
 * schmal, „Öffentliche Dokumente" breit —, und eine Zeile ohne Chip rutschte
 * ganz nach links. Untereinander gelesen ergab das keine Spalte, sondern eine
 * Treppe.
 *
 * Deshalb ein Raster mit fester erster Spalte. Sie darf sich NICHT am Inhalt
 * bemessen: jede Zeile ist ein eigener Grid-Container, `auto` würde in jeder
 * neu gemessen und die Treppe bliebe.
 */
export const ACTIVITY_LOG_COLUMNS = '116px minmax(0, 1fr)';

/** Eine Ereigniszeile. */
export const ACTIVITY_LOG_ROW: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: ACTIVITY_LOG_COLUMNS,
  columnGap: 1,
  alignItems: 'baseline',
  py: 0.4,
};

/**
 * Die Zelle, in der der Bereichs-Chip sitzt.
 *
 * Linksbündig: rechtsbündig wanderten die Chips mit der Länge ihrer
 * Beschriftung hin und her — „Vorgang" stand woanders als „Rechnungen“, und
 * damit fehlte der Spalte die Kante, die sie erst zur Spalte macht.
 */
export const ACTIVITY_LOG_CHIP_CELL: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 0.5,
  minWidth: 0,
};

/**
 * Der Bereichs-Chip.
 *
 * Er darf umbrechen: „Öffentliche Dokumente“ wurde sonst zu „Öffentliche
 * Doku…“ gekürzt — unlesbar, obwohl der Bereich genau die Angabe ist, nach
 * der gesucht wird. Zwei Zeilen sind besser als ein abgeschnittenes Wort,
 * und weil die Zeile am Schriftbild ausgerichtet ist, bleibt der Satz
 * daneben auf Höhe der ersten Chipzeile.
 *
 * Die Beschriftung steht mittig: bei zwei Zeilen ist der Chip breiter als
 * jede einzelne Zeile, und linksbündig sah „Öffentliche / Dokumente“ aus,
 * als wäre es abgerutscht. Einzeilige Chips sind so breit wie ihr Text —
 * dort ändert es nichts.
 */
export const ACTIVITY_LOG_CHIP: SxProps<Theme> = {
  height: 'auto',
  minHeight: 19,
  maxWidth: '100%',
  fontSize: '0.68rem',
  '& .MuiChip-label': {
    px: 0.75,
    py: '2px',
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    textAlign: 'center',
    lineHeight: 1.35,
  },
};

/**
 * Der senkrechte Faden, der die Zeilen eines Speichervorgangs zusammenhält.
 *
 * Ohne ihn stehen die Zeilen einer Gruppe genauso frei wie die Gruppen
 * untereinander, und bei sieben Änderungen ist nicht mehr erkennbar, wo der
 * eine Vorgang aufhört. Er sitzt unter dem Akteurssymbol der Kopfzeile.
 */
export const ACTIVITY_LOG_SPINE: SxProps<Theme> = {
  mt: 0.5,
  ml: '15px',
  pl: 2,
  borderLeft: '2px solid',
  borderColor: 'divider',
};
